import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  User, Bell, Shield, CheckCircle2, RefreshCw, Eye, EyeOff, 
  Upload, Camera, FileText, Trash2, Download, Plus, Briefcase,
  Award, ShieldCheck, File, Image, X
} from 'lucide-react';
import { toast } from 'sonner';
import SecuritySettings from '../components/SecuritySettings';

// Speciality options
const SPECIALITY_OPTIONS = [
  { value: 'occupational_therapy', label: 'Occupational Therapy' },
  { value: 'speech_language_therapy', label: 'Speech & Language Therapy' },
  { value: 'physiotherapy', label: 'Physiotherapy' },
  { value: 'psychology', label: 'Psychology' },
  { value: 'counselling', label: 'Counselling' },
  { value: 'play_therapy', label: 'Play Therapy' },
  { value: 'art_therapy', label: 'Art Therapy' },
  { value: 'music_therapy', label: 'Music Therapy' },
  { value: 'behavioural_therapy', label: 'Behavioural Therapy' },
  { value: 'sensory_integration', label: 'Sensory Integration' },
  { value: 'paediatrics', label: 'Paediatrics' },
  { value: 'neurology', label: 'Neurology' },
  { value: 'mental_health', label: 'Mental Health' },
  { value: 'administration', label: 'Administration' },
  { value: 'finance', label: 'Finance' },
  { value: 'management', label: 'Management' },
  { value: 'other', label: 'Other' }
];

export default function SettingsPage() {
  const { user, api, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const profileImageRef = useRef(null);
  const dbsDocRef = useRef(null);
  const documentRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    job_title: '',
    qualifications: '',
    hcpc_number: '',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    speciality: '',
    bio: '',
    dbs_number: '',
    dbs_expiry: ''
  });
  
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [documents, setDocuments] = useState([]);
  const [dbsDocument, setDbsDocument] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [newDocument, setNewDocument] = useState({ description: '', category: 'certificate' });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [notifications, setNotifications] = useState({
    email_appointments: true,
    email_assessments: true,
    email_invoices: false,
    push_notifications: true
  });

  // MFA State
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);

  // Load user data into form
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        job_title: user.job_title || '',
        qualifications: user.qualifications || '',
        hcpc_number: user.hcpc_number || '',
        address: user.address || '',
        emergency_contact: user.emergency_contact || '',
        emergency_phone: user.emergency_phone || '',
        speciality: user.speciality || '',
        bio: user.bio || '',
        dbs_number: user.dbs_number || '',
        dbs_expiry: user.dbs_expiry || ''
      });
      
      // Load MFA status
      setMfaEnabled(user.mfa_enabled || false);
      
      // Load profile image - prefer base64 data from database
      if (user.profile_image_data) {
        setProfileImagePreview(user.profile_image_data);
      } else if (user.profile_image) {
        // Fallback to file-based URL with cache-busting
        const baseUrl = user.profile_image.split('?')[0];
        setProfileImagePreview(`${baseUrl}?t=${Date.now()}`);
      }
      
      // Load documents
      fetchMyDocuments();
    }
  }, [user]);
  
  const fetchMyDocuments = async () => {
    try {
      const res = await api.get(`/uploads?entity_type=staff&entity_id=${user?.id}`);
      setDocuments(res.data || []);
      
      // Find DBS document
      const dbs = res.data?.find(d => d.category === 'dbs');
      if (dbs) setDbsDocument(dbs);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };
  
  // Profile Image Upload - now stores in database as base64
  const handleProfileImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }
    
    // Preview (local preview while uploading)
    const reader = new FileReader();
    reader.onloadend = () => setProfileImagePreview(reader.result);
    reader.readAsDataURL(file);
    
    // Upload using new database-storage endpoint
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/auth/upload-profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update preview with the base64 data URL from response
      if (response.data.profile_image_data) {
        setProfileImagePreview(response.data.profile_image_data);
      }
      
      toast.success('Profile photo updated successfully!');
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
      // Revert to original image
      if (user?.profile_image_data) {
        setProfileImagePreview(user.profile_image_data);
      } else if (user?.profile_image) {
        const baseUrl = user.profile_image.split('?')[0];
        setProfileImagePreview(`${baseUrl}?t=${Date.now()}`);
      } else {
        setProfileImagePreview(null);
      }
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Document Upload
  const handleDocumentUpload = async (file, category = 'certificate', description = '') => {
    if (!file) return;
    
    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'staff');
      formData.append('entity_id', user.id);
      formData.append('category', category);
      formData.append('description', description || file.name);
      
      const response = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded successfully');
      await fetchMyDocuments();
      return response.data;
    } catch (error) {
      toast.error('Failed to upload document');
      return null;
    } finally {
      setUploadingDocument(false);
    }
  };
  
  // DBS Document Upload
  const handleDbsDocumentSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    await handleDocumentUpload(file, 'dbs', `DBS Certificate - ${profileData.dbs_number || 'No Number'}`);
  };
  
  // General Document Upload
  const handleGeneralDocumentSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setNewDocument(prev => ({ ...prev, file }));
    setShowDocumentDialog(true);
  };
  
  const confirmDocumentUpload = async () => {
    if (!newDocument.file) return;
    
    await handleDocumentUpload(newDocument.file, newDocument.category, newDocument.description);
    setShowDocumentDialog(false);
    setNewDocument({ description: '', category: 'certificate' });
  };
  
  // Delete Document
  const handleDeleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/uploads/${docId}`);
      toast.success('Document deleted');
      await fetchMyDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };
  
  // Get file icon based on type
  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="w-4 h-4" />;
    if (['pdf'].includes(ext)) return <FileText className="w-4 h-4 text-red-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="w-4 h-4 text-blue-500" />;
    return <File className="w-4 h-4" />;
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const response = await api.put('/auth/me', profileData);
      toast.success('Profile updated successfully');
      
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate
    if (!passwordData.current_password) {
      toast.error('Please enter your current password');
      return;
    }
    if (!passwordData.new_password) {
      toast.error('Please enter a new password');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Toggle MFA
  const handleToggleMFA = async () => {
    setMfaLoading(true);
    try {
      if (mfaEnabled) {
        await api.post('/auth/disable-mfa');
        setMfaEnabled(false);
        toast.success('Two-Factor Authentication disabled');
      } else {
        await api.post('/auth/enable-mfa');
        setMfaEnabled(true);
        toast.success('Two-Factor Authentication enabled. You will receive a verification code via email on your next login.');
      }
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update MFA settings');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleSaveNotifications = () => {
    toast.success('Notification preferences saved');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Picture & Basic Info Card */}
            <Card className="card-icrcl lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg text-[#1e3a5f]">Profile Picture</CardTitle>
                <CardDescription>Upload a professional photo</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center space-y-4">
                <input
                  type="file"
                  ref={profileImageRef}
                  onChange={handleProfileImageSelect}
                  accept=".jpg,.jpeg,.png,.webp"
                  style={{ display: 'none' }}
                />
                <div 
                  className="w-32 h-32 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 border-4 border-[#2a9d8f] shadow-lg transition-all"
                  onClick={() => profileImageRef.current?.click()}
                >
                  {profileImagePreview ? (
                    <img src={profileImagePreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center text-slate-400">
                      <Camera className="w-10 h-10" />
                      <span className="text-xs mt-1">Add Photo</span>
                    </div>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => profileImageRef.current?.click()}
                  disabled={uploadingImage}
                  className="w-full"
                >
                  {uploadingImage ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Change Photo
                    </>
                  )}
                </Button>
                
                <div className="w-full pt-4 border-t">
                  <div className="text-center">
                    <p className="font-semibold text-[#1e3a5f]">{user?.first_name} {user?.last_name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                    <Badge className="mt-2 bg-[#2a9d8f]">{user?.role?.replace('_', ' ')}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Main Profile Form */}
            <Card className="card-icrcl lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg text-[#1e3a5f]">Profile Information</CardTitle>
                <CardDescription>Update your personal and professional details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                    data-testid="input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                    data-testid="input-last-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email (read-only)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+44 7123 456789"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    value={profileData.job_title}
                    onChange={(e) => setProfileData({ ...profileData, job_title: e.target.value })}
                    placeholder="e.g. Senior Therapist"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qualifications">Qualifications</Label>
                  <Input
                    id="qualifications"
                    value={profileData.qualifications}
                    onChange={(e) => setProfileData({ ...profileData, qualifications: e.target.value })}
                    placeholder="e.g. BSc, MSc, HCPC"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hcpc_number">HCPC Number</Label>
                  <Input
                    id="hcpc_number"
                    value={profileData.hcpc_number}
                    onChange={(e) => setProfileData({ ...profileData, hcpc_number: e.target.value })}
                    placeholder="e.g. OT12345"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="speciality">Speciality</Label>
                  <Select
                    value={profileData.speciality}
                    onValueChange={(value) => setProfileData({ ...profileData, speciality: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your speciality" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPECIALITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bio">Professional Bio</Label>
                <Textarea
                  id="bio"
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  placeholder="Tell us about your professional background, experience, and areas of expertise..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={profileData.address}
                    onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                    placeholder="Your address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qualifications">Qualifications</Label>
                  <Input
                    id="qualifications"
                    value={profileData.qualifications}
                    onChange={(e) => setProfileData({ ...profileData, qualifications: e.target.value })}
                    placeholder="e.g. BSc, MSc, HCPC"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Emergency Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact">Contact Name</Label>
                    <Input
                      id="emergency_contact"
                      value={profileData.emergency_contact}
                      onChange={(e) => setProfileData({ ...profileData, emergency_contact: e.target.value })}
                      placeholder="Emergency contact name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_phone">Contact Phone</Label>
                    <Input
                      id="emergency_phone"
                      value={profileData.emergency_phone}
                      onChange={(e) => setProfileData({ ...profileData, emergency_phone: e.target.value })}
                      placeholder="Emergency contact phone"
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleSaveProfile} 
                disabled={loading}
                className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
                data-testid="btn-save-profile"
              >
                {loading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" /> Save Changes</>
                )}
              </Button>
            </CardContent>
          </Card>
          </div>
        </TabsContent>
        
        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* DBS Section */}
            <Card className="card-icrcl">
              <CardHeader>
                <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  DBS Certificate
                </CardTitle>
                <CardDescription>Your Disclosure and Barring Service details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  type="file"
                  ref={dbsDocRef}
                  onChange={handleDbsDocumentSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  style={{ display: 'none' }}
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dbs_number">DBS Certificate Number</Label>
                    <Input
                      id="dbs_number"
                      value={profileData.dbs_number}
                      onChange={(e) => setProfileData({ ...profileData, dbs_number: e.target.value })}
                      placeholder="e.g. 001234567890"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dbs_expiry">DBS Expiry Date</Label>
                    <Input
                      id="dbs_expiry"
                      type="date"
                      value={profileData.dbs_expiry}
                      onChange={(e) => setProfileData({ ...profileData, dbs_expiry: e.target.value })}
                    />
                  </div>
                </div>
                
                {dbsDocument ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getFileIcon(dbsDocument.original_filename)}
                      <div>
                        <p className="text-sm font-medium">{dbsDocument.original_filename}</p>
                        <p className="text-xs text-slate-500">Uploaded {new Date(dbsDocument.uploaded_at).toLocaleDateString('en-GB')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" asChild>
                        <a href={dbsDocument.url || dbsDocument.file_url} target="_blank" rel="noopener noreferrer">
                          <Eye className="w-4 h-4" />
                        </a>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(dbsDocument.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full border-dashed"
                    onClick={() => dbsDocRef.current?.click()}
                    disabled={uploadingDocument}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload DBS Certificate
                  </Button>
                )}
                
                <Button 
                  onClick={handleSaveProfile} 
                  disabled={loading}
                  className="w-full bg-[#1e3a5f]"
                >
                  Save DBS Details
                </Button>
              </CardContent>
            </Card>
            
            {/* Other Documents Section */}
            <Card className="card-icrcl">
              <CardHeader>
                <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Professional Documents
                </CardTitle>
                <CardDescription>Upload certificates, qualifications, and other documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  type="file"
                  ref={documentRef}
                  onChange={handleGeneralDocumentSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
                  style={{ display: 'none' }}
                />
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => documentRef.current?.click()}
                  disabled={uploadingDocument}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Upload New Document
                </Button>
                
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {documents.filter(d => d.category !== 'dbs' && d.category !== 'profile_photo').length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No documents uploaded yet</p>
                      <p className="text-sm">Upload certificates, qualifications, etc.</p>
                    </div>
                  ) : (
                    documents.filter(d => d.category !== 'dbs' && d.category !== 'profile_photo').map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 border rounded-lg">
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.original_filename)}
                          <div>
                            <p className="text-sm font-medium">{doc.description || doc.original_filename}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{doc.category?.replace('_', ' ')}</Badge>
                              <span className="text-xs text-slate-500">{new Date(doc.uploaded_at).toLocaleDateString('en-GB')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" asChild>
                            <a href={doc.url || doc.file_url} target="_blank" rel="noopener noreferrer">
                              <Download className="w-4 h-4" />
                            </a>
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Document Upload Dialog */}
          <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Document</DialogTitle>
                <DialogDescription>Add details for your document</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>File</Label>
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    {getFileIcon(newDocument.file?.name)}
                    <span className="text-sm">{newDocument.file?.name}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={newDocument.category}
                    onValueChange={(value) => setNewDocument({ ...newDocument, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional_cert">Professional Certificate</SelectItem>
                      <SelectItem value="educational_cert">Educational Certificate</SelectItem>
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="driving_licence">Driving Licence</SelectItem>
                      <SelectItem value="visa_brp">Visa / BRP</SelectItem>
                      <SelectItem value="insurance">Insurance Document</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="training">Training Certificate</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={newDocument.description}
                    onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                    placeholder="e.g. HCPC Registration Certificate 2024"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDocumentDialog(false)}>Cancel</Button>
                <Button onClick={confirmDocumentUpload} disabled={uploadingDocument}>
                  {uploadingDocument ? 'Uploading...' : 'Upload'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          {/* Two-Factor Authentication Settings */}
          <div className="mb-6">
            <SecuritySettings />
          </div>
          
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f]">Change Password</CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Input 
                    id="current_password" 
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Enter your current password"
                    data-testid="input-current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input 
                    id="new_password" 
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Enter new password (min 6 characters)"
                    data-testid="input-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input 
                  id="confirm_password" 
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirm your new password"
                  data-testid="input-confirm-password"
                />
              </div>
              <Button 
                onClick={handleChangePassword}
                disabled={loading}
                className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
                data-testid="btn-change-password"
              >
                {loading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
                ) : (
                  'Update Password'
                )}
              </Button>
            </CardContent>
          </Card>

          <Card className="card-icrcl mt-6">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f]">Session Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Logged in as</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Active</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Role</p>
                    <p className="text-sm text-slate-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Discipline</p>
                    <p className="text-sm text-slate-500">{user?.discipline?.replace(/_/g, ' ') || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* MFA Card */}
          <Card className="card-icrcl mt-6">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                Two-Factor Authentication (MFA)
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${mfaEnabled ? 'bg-green-100' : 'bg-amber-100'}`}>
                      <Shield className={`w-5 h-5 ${mfaEnabled ? 'text-green-600' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <p className="font-medium">Email Verification Code</p>
                      <p className="text-sm text-slate-500">
                        {mfaEnabled 
                          ? 'MFA is enabled. You will receive a 6-digit code via email when you sign in.' 
                          : 'Enable to receive a verification code via email on each login.'
                        }
                      </p>
                      {mfaEnabled && (
                        <Badge className="mt-2 bg-green-100 text-green-700">Enabled</Badge>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={mfaEnabled}
                    onCheckedChange={handleToggleMFA}
                    disabled={mfaLoading}
                    data-testid="mfa-toggle"
                  />
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Why enable MFA?</strong> Two-Factor Authentication protects your account even if your password is compromised. 
                    This is especially important for healthcare platforms handling sensitive patient data.
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Session Timeout:</strong> For your security, you will be automatically logged out after 15 minutes of inactivity.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f]">Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Appointment Reminders</p>
                  <p className="text-sm text-slate-500">Receive email reminders for upcoming appointments</p>
                </div>
                <Switch
                  checked={notifications.email_appointments}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, email_appointments: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Assessment Updates</p>
                  <p className="text-sm text-slate-500">Get notified when assessments are completed</p>
                </div>
                <Switch
                  checked={notifications.email_assessments}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, email_assessments: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Invoice Notifications</p>
                  <p className="text-sm text-slate-500">Receive alerts for new invoices and payments</p>
                </div>
                <Switch
                  checked={notifications.email_invoices}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, email_invoices: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Push Notifications</p>
                  <p className="text-sm text-slate-500">Enable browser push notifications</p>
                </div>
                <Switch
                  checked={notifications.push_notifications}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, push_notifications: checked })}
                />
              </div>
              <Button onClick={handleSaveNotifications} className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
                Save Preferences
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
